import { useState, useEffect, useRef } from 'react';

interface OutletCheckoutModalProps {
  onClose: () => void;
  onConfirm: (countedAmount: number, acceptDifference: boolean) => Promise<void>;
  outletName: string;
  outletOpeningCash: number;
  remainingCash: number;
  totalCounterOpeningCash: number;
  sales: number;
  cashIn: number;
  cashOut: number;
  cashAmount: number;
  bankMobile: number;
  salesReturn: number;
  refund: number;
  creditApplied: number;
  closingCash: number;
  isLoading?: boolean;
}

export default function OutletCheckoutModal({
  onClose,
  onConfirm,
  outletName,
  outletOpeningCash,
  remainingCash,
  totalCounterOpeningCash,
  sales,
  cashIn,
  cashOut,
  cashAmount,
  bankMobile,
  salesReturn,
  refund,
  creditApplied,
  closingCash,
  isLoading = false,
}: OutletCheckoutModalProps) {
  const [countedAmount, setCountedAmount] = useState('');
  const [acceptDifference, setAcceptDifference] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const counted = countedAmount !== '' ? parseFloat(countedAmount) : 0;
  const difference = counted - closingCash;
  const hasDifference = Math.abs(difference) > 0.01;

  const canConfirm =
    countedAmount !== '' &&
    counted >= 0 &&
    (!hasDifference || acceptDifference);

  const handleConfirmRef = useRef<() => void>(() => {});
  useEffect(() => { handleConfirmRef.current = handleConfirm; });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Enter' && !isSubmitting) handleConfirmRef.current(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSubmitting]);

  const handleConfirm = async () => {
    if (!canConfirm) {
      if (hasDifference && !acceptDifference) {
        setError('Please accept the payment difference to proceed.');
      } else {
        setError('Please enter a valid counted amount.');
      }
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      await onConfirm(counted, acceptDifference);
    } catch (err: any) {
      setError(err?.message || 'Checkout failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <i className="ri-store-3-line text-xl text-gray-700"></i>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Checkout Summary of ({outletName})</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Outlet Opening Cash:{' '}
                <span className="font-semibold text-gray-700">{outletOpeningCash.toFixed(2)}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
              <span className="text-sm text-gray-500">Loading session data...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {/* Left: Session Summary */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-information-line text-blue-600"></i>
                  <p className="text-sm font-medium text-blue-900">Session Summary</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Remaining Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{remainingCash.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Counter Opening Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{totalCounterOpeningCash.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-600">+ Sales:</span>
                    <span className="text-sm font-medium text-green-600">{sales.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-600">+ Cash In:</span>
                    <span className="text-sm font-medium text-green-600">{cashIn.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Cash Out:</span>
                    <span className="text-sm font-medium text-red-600">{cashOut.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Amount:</span>
                    <span className="text-sm font-medium text-gray-900">{cashAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Bank/Mobile:</span>
                    <span className="text-sm font-medium text-red-600">{bankMobile.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Sales Return:</span>
                    <span className="text-sm font-medium text-red-600">{salesReturn.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Refund:</span>
                    <span className="text-sm font-medium text-red-600">{refund.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Credit Applied:</span>
                    <span className="text-sm font-medium text-gray-900">{creditApplied.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-300 flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-900">Closing Cash:</span>
                    <span className="text-lg font-bold text-gray-900">{closingCash.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Right: Count + Difference + Accept */}
              <div className="flex flex-col gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Count Physical Cash <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number" onWheel={(e) => e.currentTarget.blur()}
                    value={countedAmount}
                    onChange={(e) => {
                      setCountedAmount(e.target.value);
                      setAcceptDifference(false);
                      setError('');
                    }}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={isSubmitting}
                    autoFocus
                    className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-2xl font-bold disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-2">Count all bills and coins in the drawer</p>
                </div>

                {countedAmount && counted >= 0 && (
                  <div className={`rounded-lg p-4 ${hasDifference ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Difference:</span>
                      <span className={`text-2xl font-bold ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {difference > 0 ? '+' : ''}{difference.toFixed(2)}
                      </span>
                    </div>
                    {hasDifference && (
                      <p className="text-xs text-gray-600 mt-2">
                        {difference > 0 ? 'Cash over expected amount' : 'Cash short of expected amount'}
                      </p>
                    )}
                  </div>
                )}

                {hasDifference && countedAmount && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptDifference}
                      onChange={(e) => {
                        setAcceptDifference(e.target.checked);
                        setError('');
                      }}
                      disabled={isSubmitting}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">
                      Accept Payment Difference and post a profit/loss Journal Entry.
                    </span>
                  </label>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500"></i>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting || isLoading}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Processing...
              </>
            ) : (
              'Confirm and Checkout'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
